import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/feature/Navbar';
import { Footer } from '@/components/feature/Footer';
import { SEOHead } from '@/components/feature/SEOHead';
import { SchemaMarkup, breadcrumbSchema } from '@/components/feature/SchemaMarkup';
import { SITE_URL } from '@/components/feature/seoConfig';
import { productCategories, allProducts } from '@/mocks/products';

export default function ProductsPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');

  const displayedProducts =
    activeCategory === 'all'
      ? allProducts
      : productCategories.find((cat) => cat.key === activeCategory)?.products || [];

  const categoriesWithAll = [
    { key: 'all', name: t('allProducts') },
    ...productCategories.map((cat) => ({ key: cat.key, name: cat.name })),
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title={t('productsPageTitle')}
        description={t('productsPageDescription')}
        canonicalUrl={`${SITE_URL}/products`}
      />
      <SchemaMarkup
        schema={breadcrumbSchema([
          { name: t('home'), path: '/' },
          { name: t('products'), path: '/products' },
        ])}
      />
      <Navbar />

      {/* Page Header */}
      <section className="w-full px-4 md:px-8 lg:px-12 pt-28 pb-8 md:pt-32 md:pb-12 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
            <Link to="/" className="hover:text-primary-700 transition-colors">{t('home')}</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">{t('products')}</span>
          </nav>
          <h1
            className="text-2xl md:text-4xl font-bold text-foreground-950"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('productsPageTitle')}
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl">
            {t('productsPageDescription')}
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-6 md:pb-8 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2">
            {categoriesWithAll.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-3.5 py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat.key
                    ? 'bg-primary-500 text-white'
                    : 'bg-white border border-background-200/70 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-14 md:pb-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4 lg:gap-5">
            {displayedProducts.map((product) => (
              <a
                key={product.id}
                href={product.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col bg-white rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 hover:shadow-md transition-all"
              >
                <div className="relative aspect-square overflow-hidden bg-background-100">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-2.5 md:p-3 flex flex-col flex-1">
                  <h3 className="text-xs md:text-sm font-medium text-foreground-900 line-clamp-2 leading-snug flex-1 group-hover:text-primary-700 transition-colors">
                    {product.name}
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs md:text-sm font-bold text-primary-700">
                      {product.priceUsd}
                    </span>
                    <span className="text-[10px] md:text-xs text-foreground-400">
                      {product.priceCny}
                    </span>
                  </div>
                  <div className="mt-2 px-2 py-1.5 rounded-md bg-primary-50 text-center text-xs font-medium text-primary-700 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                    {t('buyNow')}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}